import { useEffect, useState, useCallback } from "react";
import { api, API_BASE, apiHref } from "@/lib/api";
import { sortMixes } from "@/lib/mixSort";
import { usePlant } from "@/context/PlantContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coins, Plus, Copy, ExternalLink, Printer, CheckCircle2, Link as LinkIcon, Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { formatLocalDateTime } from "@/lib/dates";

export default function CashSales() {
  const { plantId } = usePlant();
  const [sales, setSales] = useState([]);
  const [pending, setPending] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [pricing, setPricing] = useState({ min_load_m3: 3.0, min_load_charge: 70.0, delivery_free_km: 25.0, delivery_rate_per_km: 2.0 });
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", mix_id: "", quantity_m3: 2, price_per_m3: 220, delivery_km: "", delivery_date: today, delivery_time: "09:00", payment_method: "eft", myob_payment_method: "EFT" });
  const [creating, setCreating] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  const load = useCallback(async () => {
    const p = plantId ? `?plant_id=${plantId}` : "";
    const [s, pd, m, pr] = await Promise.all([
      api.get(`/cash-sales${p}`),
      api.get(`/cash-sales/pending${p}`).catch(() => ({ data: [] })),
      api.get("/mixes"),
      api.get("/settings/pricing").catch(() => ({ data: null })),
    ]);
    setSales(s.data);
    setPending(pd.data || []);
    setMixes(m.data);
    if (pr.data) setPricing({ ...pricing, ...pr.data });
    // eslint-disable-next-line
  }, [plantId]);

  useEffect(() => { load(); }, [load]);

  // v3.11 · Cut the deferred invoice for a walk-in job on the delivery day.
  const generateInvoiceForJob = async (job) => {
    const ok = window.confirm(
      `Cut EFT invoice for ${job.customer_name || "walk-in"}?\n\n` +
      `Delivery: ${job.date} @ ${job.time_required}\n` +
      `${job.quantity_m3}m³ · ${job.mix_code || ""}\n\n` +
      `Invoice will be dated ${job.date}. Only do this once the load is complete or the customer has arrived.`
    );
    if (!ok) return;
    try {
      const r = await api.post(`/jobs/${job.id}/generate-eft-invoice`);
      toast.success(`Invoice ${r.data.invoice_no} cut · $${r.data.total.toFixed(2)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate invoice");
    }
  };

  const create = async () => {
    if (!form.customer_name || !form.mix_id) return toast.error("Customer name and mix are required");
    if (!form.delivery_date) return toast.error("Delivery date is required");
    setCreating(true);
    try {
      const body = { ...form, plant_id: plantId };
      if (body.delivery_km === "" || body.delivery_km == null) delete body.delivery_km;
      else body.delivery_km = Number(body.delivery_km);
      const r = await api.post("/cash-sales", body);
      if (r.data.pending_invoice) {
        toast.success(`Order booked for ${r.data.delivery_date} · invoice will cut on the day of delivery ($${r.data.total.toFixed(2)})`, { duration: 7000 });
      } else {
        toast.success(`EFT sale ${r.data.invoice_no} created — $${r.data.total.toFixed(2)}`);
      }
      setLastSale(r.data);
      setOpen(false);
      setForm({ customer_name: "", customer_phone: "", customer_email: "", mix_id: "", quantity_m3: 2, price_per_m3: 220, delivery_km: "", delivery_date: today, delivery_time: "09:00", payment_method: "eft", myob_payment_method: "EFT" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const payUrl = (token) => `${window.location.origin}/pay/${token}`;

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(payUrl(token));
      toast.success("Payment link copied");
    } catch {
      toast.error("Copy failed — link: " + payUrl(token));
    }
  };

  // v3.11 — Cards are processed manually on the terminal for now.
  // "Mark Paid" flips the invoice to `paid` after the batcher has run the
  // card so today's totals reconcile without a Stripe round-trip.
  const markPaid = async (sale) => {
    const ok = window.confirm(
      `Mark sale ${sale.invoice_no} · $${sale.total.toFixed(2)} as PAID?\n\n` +
      `Only do this after the card has cleared on your terminal.`
    );
    if (!ok) return;
    try {
      await api.post(`/invoices/${sale.id}/pay`);
      toast.success(`${sale.invoice_no} marked paid`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    }
  };

  // Live invoice breakdown — mirrors backend math so the batcher sees the real total pre-save
  const subtotalConcrete = (Number(form.quantity_m3) || 0) * (Number(form.price_per_m3) || 0);
  const shortfall = Math.max(0, Number(pricing.min_load_m3) - (Number(form.quantity_m3) || 0));
  const smallLoadCharge = shortfall > 0 ? shortfall * Number(pricing.min_load_charge) : 0;
  const km = Number(form.delivery_km) || 0;
  const chargeableKm = Math.max(0, km - Number(pricing.delivery_free_km));
  const cartage = chargeableKm * Number(pricing.delivery_rate_per_km);
  const subtotal = subtotalConcrete + smallLoadCharge + cartage;
  const gst = subtotal * 0.10;
  const totalWithGst = subtotal + gst;

  return (
    <div className="p-6 lg:p-8" data-testid="cashsales-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-tag">GATE / WALK-IN</div>
          <h1 className="heading text-3xl font-black uppercase">EFT <span className="text-primary-orange">Sales</span></h1>
          <div className="text-xs text-muted-industrial mt-1">Quick invoicing for walk-in customers. Save the sale, print the docket, then process the card on your terminal manually.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-cash-sale-btn" className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
              <Plus className="w-4 h-4 mr-1" /> New EFT Sale
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="cash-sale-dialog">
            <DialogHeader><DialogTitle className="heading uppercase">New EFT Sale</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Customer Name *</Label>
                  <Input data-testid="cs-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Bob Smith" className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="label-tag">Phone</Label>
                  <Input data-testid="cs-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="0400 000 000" className="rounded-none bg-transparent border-industrial mono" />
                </div>
              </div>
              <div>
                <Label className="label-tag">Email (for receipt)</Label>
                <Input data-testid="cs-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="optional" className="rounded-none bg-transparent border-industrial mono" />
              </div>
              <div>
                <Label className="label-tag">Mix *</Label>
                <Select value={form.mix_id} onValueChange={(v) => setForm({ ...form, mix_id: v })}>
                  <SelectTrigger data-testid="cs-mix" className="rounded-none bg-transparent border-industrial"><SelectValue placeholder="Select mix" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-surface border-industrial">
                    {sortMixes(mixes).map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Delivery Date *</Label>
                  <Input
                    data-testid="cs-date"
                    type="date"
                    value={form.delivery_date}
                    onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
                <div>
                  <Label className="label-tag">Delivery Time</Label>
                  <Input
                    data-testid="cs-time"
                    type="time"
                    value={form.delivery_time}
                    onChange={(e) => setForm({ ...form, delivery_time: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-tag">Quantity (m³)</Label>
                  <Input data-testid="cs-qty" type="number" step="0.5" value={form.quantity_m3} onChange={(e) => setForm({ ...form, quantity_m3: parseFloat(e.target.value) || 0 })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="label-tag">Price / m³ ($)</Label>
                  <Input data-testid="cs-price" type="number" step="5" value={form.price_per_m3} onChange={(e) => setForm({ ...form, price_per_m3: parseFloat(e.target.value) || 0 })} className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div className="col-span-2">
                  <Label className="label-tag flex items-center justify-between">
                    <span>Delivery km (one-way, plant → site)</span>
                    <span className="text-[10px] mono text-muted-industrial normal-case tracking-normal">
                      First {pricing.delivery_free_km} km included · ${Number(pricing.delivery_rate_per_km).toFixed(2)}/km after
                    </span>
                  </Label>
                  <Input data-testid="cs-km" type="number" step="0.5" placeholder="Leave blank for gate pickup"
                         value={form.delivery_km}
                         onChange={(e) => setForm({ ...form, delivery_km: e.target.value === "" ? "" : parseFloat(e.target.value) || 0 })}
                         className="rounded-none bg-transparent border-industrial mono" />
                </div>
              </div>
              <div>
                <Label className="label-tag">Payment Method *</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="cs-payment-method">
                  {[
                    { v: "eft",  label: "EFT / Transfer" },
                    { v: "cash", label: "Cash" },
                    { v: "card", label: "Card" },
                  ].map((opt) => {
                    const active = form.payment_method === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        data-testid={`cs-pay-${opt.v}`}
                        onClick={() => setForm({ ...form, payment_method: opt.v })}
                        className={`p-2 border-2 mono text-[11px] uppercase tracking-widest transition ${
                          active
                            ? "border-primary-orange bg-primary-orange/15 text-primary-orange font-bold"
                            : "border-industrial bg-transparent text-muted-industrial hover:text-ink"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mt-1">
                  Recorded on the invoice · does NOT change the customer name
                </div>
                <label className="flex items-start gap-2 cursor-pointer border border-industrial mt-2 p-2 hover:border-primary-orange transition" data-testid="cs-myob-eft-label">
                  <input
                    type="checkbox"
                    data-testid="cs-myob-eft-cb"
                    checked={(form.myob_payment_method || "").toUpperCase() === "EFT"}
                    onChange={(e) => setForm({ ...form, myob_payment_method: e.target.checked ? "EFT" : "" })}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="mono text-[10px] uppercase tracking-widest text-ink">Link MYOB code · <span className="text-primary-orange">EFT</span></div>
                    <div className="mono text-[9px] text-muted-industrial mt-0.5">
                      Stamps the invoice's MYOB Payment Method column as <b>EFT</b> so bank rec lines up automatically on import.
                    </div>
                  </div>
                </label>
              </div>
              <div className="border-l-2 border-primary-orange pl-3 py-2 bg-primary-orange/5 space-y-0.5">
                <div className="flex justify-between mono text-xs text-muted-industrial"><span>Concrete · {Number(form.quantity_m3).toFixed(1)} m³ × ${Number(form.price_per_m3).toFixed(0)}</span><span data-testid="cs-line-concrete">${subtotalConcrete.toFixed(2)}</span></div>
                {smallLoadCharge > 0 && (
                  <div className="flex justify-between mono text-xs text-amber-500" data-testid="cs-line-shortfall">
                    <span>Min cartage · {shortfall.toFixed(2)} m³ under {Number(pricing.min_load_m3)} m³ minimum</span>
                    <span>${smallLoadCharge.toFixed(2)}</span>
                  </div>
                )}
                {cartage > 0 && (
                  <div className="flex justify-between mono text-xs text-amber-500" data-testid="cs-line-cartage">
                    <span>Delivery · {chargeableKm.toFixed(1)} chargeable km @ ${Number(pricing.delivery_rate_per_km).toFixed(2)}</span>
                    <span>${cartage.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between mono text-xs text-muted-industrial border-t border-primary-orange/20 pt-1 mt-1"><span>Subtotal (ex GST)</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between mono text-xs text-muted-industrial"><span>GST 10%</span><span>${gst.toFixed(2)}</span></div>
                <div className="flex justify-between mono text-lg font-bold text-primary-orange mt-1"><span>TOTAL</span><span data-testid="cs-total">${totalWithGst.toFixed(2)}</span></div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="cs-save-btn" disabled={creating} onClick={create} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
                <Save className="w-4 h-4 mr-1" /> {creating ? "Saving…" : "Save Sale"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {lastSale && (
        <div className="card-industrial p-4 mb-6 border-l-2 border-primary-orange" data-testid="last-sale-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="label-tag text-primary-orange">JUST CREATED</div>
              <div className="heading text-xl font-black">{lastSale.invoice_no} · <span className="text-primary-orange">${lastSale.total.toFixed(2)}</span></div>
              <div className="text-xs text-muted-industrial mt-1">Print the docket, run the card on your terminal, then hit <b className="text-primary-orange">Mark Paid</b>.</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="banner-mark-paid"
                onClick={() => markPaid(lastSale)}
                className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Paid
              </Button>
              <a
                href={apiHref(`/invoices/${lastSale.id}/pdf`)}
                target="_blank"
                rel="noreferrer"
                data-testid="banner-print-pdf"
                className="inline-flex items-center px-3 py-2 rounded-none border border-industrial uppercase tracking-widest text-xs font-bold text-ink hover:border-primary-orange hover:text-primary-orange"
              >
                <Printer className="w-3.5 h-3.5 mr-1" /> Print Docket
              </a>
              <Button
                disabled
                data-testid="banner-open-pay"
                title="Frozen — payment link disabled while cards are processed manually. Re-enable in Settings when you go online."
                className="rounded-none bg-surface-2 text-muted-industrial cursor-not-allowed border border-industrial uppercase tracking-widest text-xs font-bold"
              >
                <Lock className="w-3.5 h-3.5 mr-1" /> Pay Link
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* v3.11 · Pending EFT orders (booked-ahead, invoice deferred until delivery day) */}
      {pending.length > 0 && (
        <div className="card-industrial overflow-hidden mb-6 border-l-2 border-amber-500" data-testid="pending-eft-orders">
          <div className="px-4 py-3 border-b border-industrial bg-amber-950/20 flex items-center gap-2">
            <div className="mono text-[10px] uppercase tracking-widest text-amber-300 font-bold">Pending EFT Orders · {pending.length}</div>
            <div className="mono text-[10px] text-muted-industrial ml-auto">Invoice cuts on delivery day (or when you tap the button)</div>
          </div>
          <div className="divide-y divide-industrial">
            {pending.map((j) => (
              <div key={j.id} className="grid grid-cols-12 gap-2 px-4 py-2 items-center text-xs" data-testid={`pending-job-${j.id}`}>
                <div className="col-span-2 mono text-amber-300 font-bold">{j.date} {j.time_required}</div>
                <div className="col-span-3 uppercase font-bold">{j.customer_name || (j.walk_in_customer?.name) || "Walk-in"}</div>
                <div className="col-span-2 mono text-muted-industrial">{j.mix_code || "—"}</div>
                <div className="col-span-1 mono text-right">{j.quantity_m3}m³</div>
                <div className="col-span-2 mono text-right text-primary-orange font-bold">${((Number(j.price_per_m3) || 0) * (Number(j.quantity_m3) || 0)).toFixed(2)}</div>
                <div className="col-span-2 text-right">
                  <button
                    onClick={() => generateInvoiceForJob(j)}
                    data-testid={`gen-invoice-${j.id}`}
                    className="mono text-[10px] uppercase tracking-widest bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black px-2.5 py-1 font-bold"
                    title="Cut the EFT invoice now (use on the delivery day after the load is complete)"
                  >
                    Cut Invoice
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-industrial overflow-hidden">
        <div className="grid grid-cols-6 gap-2 px-4 py-2 label-tag border-b border-industrial bg-surface-2">
          <div>Date</div>
          <div>Invoice</div>
          <div className="col-span-2">Customer</div>
          <div>Total</div>
          <div className="text-right">Status / Actions</div>
        </div>

        {sales.length === 0 && (
          <div className="p-10 text-center text-muted-industrial">No EFT sales yet.</div>
        )}
        {sales.map((s) => (
          <div key={s.id} className="grid grid-cols-6 gap-2 px-4 py-3 border-b border-industrial items-center text-sm" data-testid={`cs-row-${s.invoice_no}`}>
            <div className="mono text-xs text-muted-industrial">{formatLocalDateTime(s.created_at)}</div>
            <div className="mono text-primary-orange">{s.invoice_no}</div>
            <div className="col-span-2 truncate">
              {s.customer_name}
              {s.payment_method && (
                <span
                  className={`ml-2 mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border align-middle ${
                    s.payment_method === "eft"  ? "border-blue-500 text-blue-400" :
                    s.payment_method === "card" ? "border-purple-500 text-purple-400" :
                                                  "border-emerald-500 text-emerald-400"
                  }`}
                  data-testid={`cs-method-${s.invoice_no}`}
                >
                  {s.payment_method}
                </span>
              )}
            </div>
            <div className="mono">${s.total.toFixed(2)}</div>
            <div className="flex items-center justify-end gap-2 text-xs uppercase">
              <span className={`mono px-1.5 py-0.5 border ${s.status === "paid" ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>{s.status}</span>
              {s.status !== "paid" && (
                <button
                  data-testid={`mark-paid-${s.invoice_no}`}
                  onClick={() => markPaid(s)}
                  className="text-primary-orange hover:text-ink inline-flex items-center gap-1 font-bold"
                  title="Mark this sale as paid after processing the card on your terminal"
                >
                  <CheckCircle2 className="w-3 h-3" /> Mark Paid
                </button>
              )}
              {s.status !== "paid" && (
                <span
                  data-testid={`copy-link-${s.invoice_no}`}
                  className="text-muted-industrial/60 inline-flex items-center gap-1 cursor-not-allowed"
                  title="Frozen — payment link disabled while cards are processed manually"
                >
                  <Lock className="w-3 h-3" /> Link
                </span>
              )}
              {s.status !== "paid" && (
                <span
                  data-testid={`open-pay-${s.invoice_no}`}
                  className="text-muted-industrial/60 inline-flex items-center gap-1 cursor-not-allowed"
                  title="Frozen — online pay page disabled while cards are processed manually"
                >
                  <Lock className="w-3 h-3" /> Pay
                </span>
              )}
              <a href={apiHref(`/invoices/${s.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`pdf-${s.invoice_no}`} className="text-ink hover:text-primary-orange inline-flex items-center gap-1"><Printer className="w-3 h-3" /> PDF</a>
              {s.status === "paid" && <CheckCircle2 className="w-3.5 h-3.5 text-primary-orange" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
